import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { Professional } from '../../interfaces';
import { BusinessService } from '../../../services/business';
import { AgendaService } from '../../../services/business';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { AgendaFilterService, ToastService } from '../../services';
import { MembershipService } from '../../../services/membership/membership.service';
import { GetAgendaEventsParams } from '../../interfaces';
import hexToRgba from '../../common/utils';

export interface ProfessionalWithEventCount extends Professional {
  eventCount: number;
  isLoadingCount: boolean;
}

@Component({
  selector: 'app-professional-list',
  imports: [CommonModule],
  templateUrl: './professional-list.html',
  styleUrl: './professional-list.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfessionalList extends BaseAuthComponent implements OnInit {
  isCollapsed = input<boolean>(false);
  isLgUp = input<boolean>(false);
  startDate = input<Date | string | null>(null); // Date range for event counting
  endDate = input<Date | string | null>(null);

  private readonly businessService = inject(BusinessService);
  private readonly agendaService = inject(AgendaService);
  private membershipService = inject(MembershipService);
  private toastService = inject(ToastService);
  professionals = signal<ProfessionalWithEventCount[]>([]);
  private filterSvc = inject(AgendaFilterService);
  private hiddenIds = signal<Set<string>>(new Set());
  fullMode = signal(false);
  trackById = (_: number, p: ProfessionalWithEventCount) => p.userId;
  visibleIds = computed(() =>
    this.professionals()
      .filter((p) => !this.hiddenIds().has(p.userId))
      .map((p) => p.userId)
  );
  isHidden = (id: string) => this.hiddenIds().has(id);
  hiddenCount = computed(() => this.hiddenIds().size);

  constructor() {
    super();
    effect(() => {
      if (this.isCollapsed() && !this.isLgUp()) {
        this.fullMode.set(true);
      } else {
        this.fullMode.set(false);
      }
    });
  }

  ngOnInit(): void {
    // Only load professionals if membership is not expired
    if (!this.membershipService.isMembershipExpired()) {
      this.load();
      // Calculate current week dates if not provided
      if (!this.startDate() || !this.endDate()) {
        this.calculateCurrentWeek();
      }
    }
  }

  private calculateCurrentWeek(): void {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    // Calculate Monday of current week (if Sunday, go back 6 days; otherwise go back to Monday)
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    // Update the component to use these dates
    // Since inputs are read-only, we'll use them in loadEventCounts
    this.weekStart = monday;
    this.weekEnd = sunday;
  }

  private weekStart: Date | null = null;
  private weekEnd: Date | null = null;

  load() {
    // Additional defensive check before making API call
    if (this.membershipService.isMembershipExpired()) {
      return;
    }

    // Calculate current week if not provided
    if (!this.startDate() || !this.endDate()) {
      this.calculateCurrentWeek();
    }

    // Load professionals
    this.businessService
      .searchEntityByBusinessId(this.businessId, 'professionals', '', 1, 100)
      .subscribe({
        next: (list) => {
          const professionalsWithCounts: ProfessionalWithEventCount[] = list.map(
            (p) => ({
              ...p,
              eventCount: 0,
              isLoadingCount: true,
            })
          );
          this.professionals.set(professionalsWithCounts);

          // The filter outlives this list (it is recreated when the sidebar re-renders):
          // restore which professionals were hidden so the checkboxes match the calendar
          const shown = this.filterSvc.filteredIds();
          if (shown.length) {
            this.hiddenIds.set(
              new Set(professionalsWithCounts.map((p) => p.userId).filter((id) => !shown.includes(id)))
            );
          }

          // Load event counts for each professional
          this.loadEventCounts();
        },
        error: (e) => {
          console.error('Error loading professionals:', e);
          this.toastService.show('Error al cargar profesionales', 'error');
        },
      });
  }

  private loadEventCounts() {
    let start = this.startDate();
    let end = this.endDate();

    // If no date range provided, use current week
    if (!start || !end) {
      if (!this.weekStart || !this.weekEnd) {
        this.calculateCurrentWeek();
      }
      start = this.weekStart;
      end = this.weekEnd;
    }

    // If still no dates, skip counting
    if (!start || !end) {
      this.professionals.update((list) =>
        list.map((p) => ({ ...p, isLoadingCount: false, eventCount: 0 }))
      );
      return;
    }

    // Load event counts for each professional
    this.professionals().forEach((professional) => {
      const params: GetAgendaEventsParams = {
        professionalId: professional.userId,
        businessId: this.businessId,
        startDate: start instanceof Date ? start : new Date(start),
        endDate: end instanceof Date ? end : new Date(end),
      };

      this.agendaService.getAgendaEvents(params).subscribe({
        next: (events) => {
          this.professionals.update((list) =>
            list.map((p) =>
              p.userId === professional.userId
                ? { ...p, eventCount: events.length, isLoadingCount: false }
                : p
            )
          );
        },
        error: (err) => {
          console.error(
            `Error loading event count for professional ${professional.userId}:`,
            err
          );
          this.professionals.update((list) =>
            list.map((p) =>
              p.userId === professional.userId
                ? { ...p, eventCount: 0, isLoadingCount: false }
                : p
            )
          );
        },
      });
    });
  }

  onFilterProfessionals(professionalId: string) {
    if (!professionalId) return;

    const next = new Set(this.hiddenIds());
    if (next.has(professionalId)) next.delete(professionalId);
    else next.add(professionalId);
    this.hiddenIds.set(next);
    this.applyFilter();
  }

  /** Shows a single professional's appointments and hides the rest */
  showOnly(professionalId: string) {
    this.hiddenIds.set(
      new Set(this.professionals().map((p) => p.userId).filter((id) => id !== professionalId))
    );
    this.applyFilter();
  }

  showAll() {
    this.hiddenIds.set(new Set());
    this.applyFilter();
  }

  private applyFilter() {
    // An empty list means "no filter" for the calendar, so hiding everyone
    // needs an id that matches no appointment
    const visible = this.visibleIds();
    if (!this.hiddenIds().size) this.filterSvc.filterAgendas([]);
    else this.filterSvc.filterAgendas(visible.length ? visible : ['__none__']);
  }

  initials(professional: Professional): string {
    return this.getProfessionalDisplayName(professional)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('');
  }

  getProfessionalDisplayName(professional: Professional): string {
    if (professional.firstName || professional.lastName) {
      return `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
    }
    return professional.email || 'Profesional';
  }

  // Expose hexToRgba for template use
  hexToRgba = hexToRgba;
}

